import Button from '../../../components/common/button'
import { LanguageDTO, UserDTO } from '@ringo/shared'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useUser } from '../../../common/hooks/auth/useUser'
import { useTranslation } from '../../../useTranslation'
import { toast } from 'react-toastify'
import ControlledDropdown from '../../../components/form/controlled-dropdown'
import { useMutation } from '@tanstack/react-query'
import { useContext, useEffect, useMemo } from 'react'
import { object } from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useValidationRules } from '../../../common/validation-rules'
import { Validation } from '../../../common/types'
import { useUserApi } from '../../../common/api-context'
import { APIError } from '@ringo/types'
import { GlobalContext } from '../../../common/global-context'

const EditLanguageForm = () => {
    const { user, refreshUserData, updateUserLanguage } = useUser()
    const { t, supportedLanguages } = useTranslation()
    const { settings } = useContext(GlobalContext)
    const validationRules = useValidationRules()
    const userApi = useUserApi()

    const validationObject: Validation<UserDTO> = {
        langKey: validationRules.language,
        docLangKey: validationRules.language,
    }

    const schema = object(validationObject)

    const { handleSubmit, control, setValue, getValues } = useForm<UserDTO>({
        resolver: yupResolver(schema as any),
        defaultValues: user as UserDTO,
    })

    useEffect(() => {
        // used to set form value if language in app header is changed
        if (user && user?.langKey !== getValues('langKey')) {
            setValue('langKey', user.langKey!.toUpperCase() as any)
        }
    }, [user?.langKey])

    const supportedDocLanguages = useMemo(() => {
        return supportedLanguages.filter((x) => x.id === 'EN' || x.id === 'DE')
    }, [supportedLanguages])

    const uiSupportedLanguages = useMemo(() => {
        return supportedLanguages.filter((x) => settings.activeLanguages?.includes(x.id as any)).map((x) => ({ ...x, id: x.id }))
    }, [supportedLanguages, settings])

    const mutation = useMutation({
        mutationFn: async (formData: UserDTO) => {
            updateUserLanguage(formData.langKey?.toLowerCase() as any)
            return userApi.updateLanguage(formData as LanguageDTO)
        },
        onSuccess: () => {
            refreshUserData()
            toast.success(t('myAccount.personalData.userDetailsUpdatedSuccessfully'))
        },
        onError: (error: APIError) => {
            const errorMsg = error?.response?.data?.detail
            toast.error(errorMsg ?? t('errorUpdatingData'))
        },
    })

    const updatePersonalData: SubmitHandler<UserDTO> = async (data) => {
        mutation.mutate(data)
    }

    return (
        <form onSubmit={handleSubmit(updatePersonalData)}>
            <div className="pt-4 pb-12 max-tablet:pb-2">
                <div className="flex flex-col mb-3">
                    <div className="w-full mb-3">
                        <div className="text-left relative">
                            <ControlledDropdown label={t('displayLanguage')} required options={uiSupportedLanguages} name="langKey" control={control} />
                        </div>
                    </div>
                    <div className="w-full">
                        <div className="text-left relative">
                            <ControlledDropdown
                                label={t('preferredDocumentLanguage')}
                                required
                                options={supportedDocLanguages}
                                name="docLangKey"
                                control={control}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex justify-center">
                <Button type="squareBlue" icon="check" text={t('save')} isLoading={mutation.isPending} />
            </div>
        </form>
    )
}

export default EditLanguageForm
